/**
 * =========================================================================================
 * Component - Add Column
 * =========================================================================================
 * Reusable drag-drop column visibility selector for UTable.
 **/

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Controller, useFormContext} from "react-hook-form";
import * as Icon from 'ubs-icons';
import UCheckBox from '../../../UCheckBox';
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import UFilterSearch from '../../UFilters/UFilterSearch/UFilterSearch';

const MemoizedDraggable = React.memo(Draggable);
const MemoizedDroppable = React.memo(Droppable);

const AddColumn = (props) => {
    const { control, watch, setValue } = useFormContext();
    const [columnList, setColumnList] = useState(props.column || []);
    const [searchText, setSearchText] = useState('');
    const isDraggable = props.isDraggable !== false; // Default to true
    const watchedValues = watch();
    const containerRef = useRef(null);

    const isChecked = (c) => {
        const v = watchedValues?.[c?.value];
        return v !== undefined ? v : c?.status;
    };

    const selectedCount = columnList.reduce((count, c) => {
        if (c?.isMandatory) return count;
        return isChecked(c) ? count + 1 : count;
    }, 0);
    const maxSelect = Number(props?.maxSelect) > 0 ? Number(props?.maxSelect) : 0;
    const limitReached = maxSelect > 0 && selectedCount >= maxSelect;

    const isSearching = searchText.trim().length > 0;
    const filteredList = useMemo(() => {
        if (!isSearching) return columnList;
        const q = searchText.trim().toLowerCase();
        return columnList.filter(c => (c?.label || '').toLowerCase().includes(q));
    }, [columnList, searchText, isSearching]);

    const filteredSet = useMemo(() => new Set(filteredList.map(c => c?.value)), [filteredList]);

    const togglableInView = filteredList.filter(c => !c?.isMandatory && !c?.isLocked);
    const checkedInViewCount = togglableInView.reduce((count, c) => isChecked(c) ? count + 1 : count, 0);
    const allSelectedInView = togglableInView.length > 0 && checkedInViewCount === togglableInView.length;

    const handleSelectAll = (checked) => {
        togglableInView.forEach(c => {
            setValue(c?.value, checked);
            props?.onFieldChange?.(c?.value, checked);
        });
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        background: isDragging ? "#E9EBF8" : "",
        ...(isDragging && {padding: '10px 3px', borderRadius: '5px'}),
        ...draggableStyle
    });

    useEffect(() => {
        if (props?.column) {
            let columns = props?.column;
            if (props?.savedOrder && props?.savedOrder?.length > 0) {
                const sorted = [];
                props?.savedOrder.forEach(key => {
                    const col = columns?.find(c => c?.value === key);
                    if (col) sorted.push(col);
                });
                columns.forEach(col => {
                    if (!props.savedOrder.includes(col.value)) {
                        sorted.push(col);
                    }
                });
                columns = sorted;
            }

            setColumnList(columns);
        }
    }, [props?.savedOrder, props?.tabKey]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (containerRef.current) {
                const scrollContainer = containerRef.current.closest('.u_side-modal-body');
                if (scrollContainer) scrollContainer.scrollTop = 0;
            }
        }, 0);
        
        return () => clearTimeout(timeoutId);
    }, [columnList.length]);

    const handleDragEnd = (result) => {
        const {source, destination} = result;
        if (!destination) return;

        const draggedItem = columnList[source.index];
        const targetItem = columnList[destination.index];

        // Prevent moving a locked column or dropping into a locked position
        if (draggedItem?.isLocked || targetItem?.isLocked) return;

        const ssi = source?.index;
        const dsi = destination?.index;
        if (ssi !== dsi) {
            const newColumnList = Array.from(columnList);
            const [movedItem] = newColumnList.splice(source.index, 1);
            newColumnList.splice(destination.index, 0, movedItem);
            setColumnList(newColumnList);
            if (props?.onColumnReorder) {
                props.onColumnReorder(newColumnList);
            }
            if (props?.addColumn) { props.addColumn(newColumnList); }
        }
    };


    return (
        <React.Fragment>
            {maxSelect > 0 && (
                <p className='u_small-font u_color-orange u_px-20 u_pt-15'>You can display a maximum of {maxSelect} fields only.</p>
            )}
                
            <div className="u_sticky-top u_bg-white u_px-20 u_pt-15 u_pb-10">
                <div className="u_select-search u_mb-5">
                    <UFilterSearch
                        placeholder="Search"
                        search={searchText}
                        onSearch={setSearchText}
                        minLength={1}
                    />
                </div>
            </div>
            {togglableInView.length > 0 && maxSelect <= 0 && (
                    <div className="u_flex u_align-center u_px-20">
                        <UCheckBox
                            id="u_select_all_columns"
                            label="Select All"
                            className="u_pl-24"
                            checked={allSelectedInView}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                    </div>
                )}
            <div className="u_px-20 u_py-10" ref={containerRef}>
                {filteredList.length === 0 && (
                    <p className="u_small-font u_text-c u_color-gray u_py-20 u_flex-align u_content-center ">No columns found.</p>
                )}
                <DragDropContext onDragEnd={handleDragEnd}>
                    <MemoizedDroppable droppableId='columns' type="columns">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {columnList.map((c, i) => {
                                    const isVisible = filteredSet.has(c.value);
                                    return (
                                        <MemoizedDraggable
                                            key={c?.value || i}
                                            draggableId={c?.value || i.toString()}
                                            index={i}
                                            isDragDisabled={isSearching || !isDraggable || c?.isLocked}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                    className={`u_mb-16 ${isVisible ? 'u_flex-align' : 'u_hidden'}`}
                                                >
                                                    {(c.isLocked
                                                        ? <Icon.Lock size={16} className="u_mr-8 u_flex-shrink" fill="#999"/>
                                                        : <Icon.ThreeDotsVertical size={16} className="u_mr-8 u_flex-shrink" style={{cursor: 'grab'}}/>
                                                    )}
                                                    <Controller
                                                        control={control}
                                                        name={c.value}
                                                        defaultValue={c.status}
                                                        render={({ onChange, value }) => {
                                                            const currentlyChecked = c.isMandatory ? true : (value ?? c.status);
                                                            const blockedByLimit = !c.isMandatory && limitReached && !currentlyChecked;
                                                            const disabled = c.isMandatory || blockedByLimit;
                                                            return (
                                                                <UCheckBox
                                                                    disableClass={disabled ? 'u_checkbox-disable' : ''}
                                                                    id={c.label}
                                                                    label={c.label}
                                                                    checked={currentlyChecked}
                                                                    onChange={(e) => {
                                                                        if (disabled) return;
                                                                        onChange && onChange(e.target.checked);
                                                                        props?.onFieldChange?.(c.value, e.target.checked);
                                                                    }}
                                                                    className=""
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </MemoizedDraggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </MemoizedDroppable>
                </DragDropContext>
            </div>
        </React.Fragment>
    );
};

export default AddColumn;
